import { useState, useReducer, useEffect } from 'react';
import { useFetch } from './useFetch';
import _ from 'lodash';
import { AddressLookup } from '../store/actions';
const alphaNumeric = (a: string, b: string) => a.localeCompare(b, 'no', { numeric: true });

type FormState = {
  zipCode: {
    inputValue: string;
    selectedValue: string;
    index: number;
  };
  streetName: {
    inputValue: string;
    selectedValue: string;
    index: number;
  };
  streetNumber: {
    inputValue: string;
    selectedValue: string;
    index: number;
  };
  floor: {
    inputValue: string;
    selectedValue: string;
    index: number;
  };
  apartment: {
    inputValue: string;
    selectedValue: string;
    index: number;
  };
};
const initState = {
  zipCode: {
    index: 0,
    inputValue: '',
    selectedValue: ''
  },
  streetName: {
    index: 1,
    inputValue: '',
    selectedValue: ''
  },
  streetNumber: {
    index: 2,
    inputValue: '',
    selectedValue: ''
  },
  floor: {
    index: 3,
    inputValue: '',
    selectedValue: ''
  },
  apartment: {
    index: 4,
    inputValue: '',
    selectedValue: ''
  }
};

type FormAction = {
  type: 'SET_INPUT_VALUE' | 'SET_SELECTED_VALUE';
  payload: {
    name: keyof FormState;
    value: string;
  };
};

export type AddressSearchValue = {
  zipCodeData: {
    region?: string;
    loading?: boolean;
    error?: boolean;
  };
  streetNames?: string[];
  streetNumbers?: string[];
  floors?: string[];
  apartments?: string[];
  validAddress: AddressLookup | undefined;
  setValidAddress: (address: AddressLookup | undefined) => void;
  formState: FormState;
  setSelectedValue: (name: keyof FormState) => (value: string) => void;
  setInputValue: (name: keyof FormState) => (value: string) => void;
  oneFloorOneUnit: boolean;
  oneUnitInSelectedFloor: boolean;
};

export const useAddressSearch = () => {
  const [validAddress, setValidAddress] = useState<AddressLookup | undefined>(undefined);

  const reducer = (state: FormState, action: FormAction) => {
    const { name, value } = action.payload;
    if (action.type === 'SET_INPUT_VALUE') {
      setValidAddress(undefined);
      const currentIndex = state[name].index;
      const newState = { ...state };

      (Object.keys(newState) as Array<keyof FormState>).forEach(key => {
        if (newState[key].index > currentIndex) {
          newState[key].inputValue = '';
          newState[key].selectedValue = '';
        }
      });

      return {
        ...newState,
        [name]: {
          ...newState[name],
          selectedValue: '',
          inputValue: value
        }
      };
    } else if (action.type === 'SET_SELECTED_VALUE') {
      return {
        ...state,
        [name]: {
          ...state[name],
          selectedValue: value,
          inputValue: value
        }
      };
    }
    return state;
  };

  const [state, dispatch] = useReducer(reducer, initState);

  const setInputValue = (name: keyof FormState) => (value: string) => {
    dispatch({
      type: 'SET_INPUT_VALUE',
      payload: {
        name: name,
        value: value
      }
    });
  };
  const setSelectedValue = (name: keyof FormState) => (value: string) => {
    dispatch({
      type: 'SET_SELECTED_VALUE',
      payload: {
        name: name,
        value: value
      }
    });
  };

  const zipCode = state.zipCode.inputValue;

  const zipCodeUrl = `/adressesok/api/address/zipcode?postalCode=${zipCode}`;
  const { data: zipCodeResult, loading, error } = useFetch(zipCodeUrl, zipCode.length !== 4);

  const region =
    zipCodeResult?.data?.map((item: { postalRegion: string; postalCode: string }) => item.postalRegion)?.[0] ?? '';

  useEffect(() => {
    const zipCodeSelected = zipCode.length === 4 && region.length > 1;

    if (zipCodeSelected) {
      dispatch({
        type: 'SET_SELECTED_VALUE',
        payload: {
          name: 'zipCode',
          value: state.zipCode.inputValue
        }
      });
    }
  }, [zipCode, region]);

  const zipCodeData = {
    region,
    loading,
    error
  };

  // Streetnames
  const streetNameUrl = `/adressesok/api/address/street?streetName=${''}&postalCode=${zipCode}`;
  const { data } = useFetch(streetNameUrl, zipCode.length !== 4);

  const streetNames = (data?.data?.map((street: { streetName: string }) => street.streetName) as string[])?.filter(s =>
    s.startsWith(state.streetName.inputValue.toUpperCase())
  );

  // All Valid address IDs in street with zipcode
  const fetchAddressDetailsUrl = `/adressesok/api/address/search?streetName=${state.streetName.selectedValue}&postalCode=${zipCode}`;
  const { data: addressDetails } = useFetch(fetchAddressDetailsUrl, !state.streetName.selectedValue);

  const streetNumberGroup = _.groupBy(
    addressDetails?.data ?? [],
    address => address.streetNumber + address.streetLetter ?? ''
  );
  const streetNumbers = Object.keys(streetNumberGroup)
    .filter(s => s.startsWith(state.streetNumber.inputValue.toUpperCase()))
    .sort(alphaNumeric);

  // Floor
  const floorsGroup = _.groupBy(streetNumberGroup[state.streetNumber.selectedValue], addressNumber =>
    addressNumber.floor === 'Underetasje' ? 0 : addressNumber.floor
  );

  const numberOfFloors = Object.keys(floorsGroup).length;
  const floors = Object.keys(floorsGroup)
    .filter(s => s.startsWith(state.floor.inputValue.toUpperCase()))
    .sort(alphaNumeric);

  // Apartment
  const apartmentGroups = _.groupBy(floorsGroup[state.floor.selectedValue], floor => floor.apartment);

  const apartments = Object.keys(apartmentGroups);
  apartments.sort(alphaNumeric);

  const onlyOneApartment = apartments.length === 1;
  const oneFloorOneUnit = numberOfFloors === 1 && floorsGroup[floors[0]] && floorsGroup[floors[0]].length === 1;

  const oneUnitInSelectedFloor = onlyOneApartment;

  useEffect(() => {
    if (oneFloorOneUnit) {
      const selectedAddress = floorsGroup[floors[0]][0];
      return setValidAddress(selectedAddress);
    }
    if (oneUnitInSelectedFloor) {
      const selectedAddress = floorsGroup[state.floor.selectedValue][0];
      return setValidAddress(selectedAddress);
    }
    if (state.apartment.selectedValue) {
      const selectedAddress = apartmentGroups[state.apartment.selectedValue][0];
      return setValidAddress(selectedAddress);
    }
  }, [state.apartment.selectedValue, oneFloorOneUnit, oneUnitInSelectedFloor]);

  const value = {
    zipCodeData,
    streetNames,
    streetNumbers,
    floors,
    apartments,
    validAddress,
    setValidAddress,
    formState: state,
    setSelectedValue,
    setInputValue,
    oneFloorOneUnit,
    oneUnitInSelectedFloor
  };
  return value;
};
